import { LocalDate } from '@js-joda/core';
import type { QueryClient } from '@tanstack/query-core';
import type { SecurityMasterService } from 'src/services/SecurityMasterService';
import type { TokenState } from 'src/store/reducers/auth';

const FIVE_MINUTES = 1000 * 60 * 5;

export const getSecurityMasterAccessKeyQuery = (
    accessToken: TokenState | null | undefined,
    queryClient: QueryClient,
    securityMasterService: SecurityMasterService,
) => ({
    queryFn: async () => {
        const key = await securityMasterService.fetchSecurityMaster(accessToken!.token, LocalDate.now(), []);

        queryClient.invalidateQueries({
            queryKey: [key],
        });

        return key;
    },
    queryKey: ['security-master-accessKey'],
    refetchInterval: FIVE_MINUTES,
    enabled: Boolean(accessToken?.token),
    placeholderData: 'SECURITY_MASTER',
});

export const getSecurityMasterEntryQuery = (
    securityMasterAccessKey: string | undefined,
    securityMasterService: SecurityMasterService,
    symbol: string | undefined,
) => ({
    queryFn: () => securityMasterService.fetchSecurityMasterEntry(symbol!),
    queryKey: [securityMasterAccessKey, 'ENTRY', symbol!],
    enabled: Boolean(securityMasterAccessKey) && Boolean(symbol),
});

export const getSecurityMasterEntriesQuery = (
    securityMasterAccessKey: string | undefined,
    securityMasterService: SecurityMasterService,
    symbols: string[],
) => ({
    queryFn: () => securityMasterService.fetchSecurityMasterEntries(symbols).then((x) => Object.values(x)),
    queryKey: [securityMasterAccessKey, 'BULK_ENTRY', ...symbols],
    enabled: Boolean(securityMasterAccessKey) && symbols.length > 0,
});

export const getSymbolsMatchingPrefixQuery = (
    securityMasterAccessKey: string | undefined,
    securityMasterService: SecurityMasterService,
    prefix: string,
) => ({
    queryFn: () => securityMasterService.fetchSymbolsMatchingPrefix(prefix),
    queryKey: [securityMasterAccessKey, 'PREFIX', prefix],
    enabled: Boolean(securityMasterAccessKey) && Boolean(prefix),
});

export const getRandomSymbolsQuery = (
    securityMasterAccessKey: string | undefined,
    securityMasterService: SecurityMasterService,
    count: number,
) => ({
    queryFn: () => securityMasterService.fetchRandomSymbols(count),
    queryKey: [securityMasterAccessKey, 'RANDOM', count],
    enabled: Boolean(securityMasterAccessKey) && count > 0,
});
